import { LoadingSpinner, Toast } from "@newsela/angelou";
import { actionNames, objectTypes } from "@newsela/snitch";
import { useRouter } from "next/router";
import PropTypes from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";

import { getAssignmentContentIds } from "@/assignments/utils/assignmentUtils";
import useChallengeArticleContext from "@/challenges/utils/useChallengeArticleContext";
import { LANG_TOGGLE_LIST } from "@/constants/Language";
import logSubmissionError from "@/monitoring/logActivitySubmissionError";
import focusElementRefKeys from "@/view/constants/focusElementRefKeys";
import getCurrentQuizQuestions from "@/view/utils/getCurrentQuizQuestions";
import useFocusManager from "@/view/utils/useFocusManager";
import useFormativeWebTokenGet from "@/view/utils/useFormativeWebTokenGet";
import useLazyStudentQuizGet from "@/view/utils/useLazyStudentQuizGet";
import useQuestionFlagPost from "@/view/utils/useQuestionFlagPost";
import useStudentQuizCreate from "@/view/utils/useStudentQuizCreate";
import {
  panelWrapperCss,
  scrollContainerCss,
} from "../ActivityPanel/ActivityPanel.style";
import FormativeBanner from "../FormativeBanner";
import ItemCount from "../ItemCount";
import NavigationButtons from "../NavigationButtons";
import PanelHeader from "../PanelHeader";
import QuestionFlagButton from "../QuestionFlagButton";
import QuestionFlagContainer from "../QuestionFlagContainer";
import QuizQuestionText from "../QuizQuestion/extensions/QuizQuestionText";
import QuizQuestion from "../QuizQuestion/QuizQuestion.tsx";
import QuizSummary from "../QuizSummary";
import QuizToggleAnswersButton from "../QuizToggleAnswersButton";
import QuizWarningMessage from "../QuizWarningMessage";
import getAlignedQuizQuestionsAndAnswers from "../utils/getAlignedQuizQuestionsAndAnswers";
import getAssignmentSetting from "../utils/getAssignmentSetting";
import {
  containerCss,
  titleCss,
  walledTeacherToastCSS,
} from "./QuizContainer.style";

const propTypes = {
  closeButtonEventProperties: PropTypes.object,
  currentAssignment: PropTypes.object,
  language: PropTypes.oneOf(LANG_TOGGLE_LIST),
  data: PropTypes.object,
  inProgressQuizzes: PropTypes.object,
  isBannerModeActive: PropTypes.bool,
  contentId: PropTypes.string,
  onClickCloseButton: PropTypes.func,
  shouldFocusCloseButtonRef: PropTypes.object,
  unlockLevel: PropTypes.func,
  updateinProgressQuizzes: PropTypes.func,
  userProperties: PropTypes.object,
  assignments: PropTypes.array,
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    unique_id: PropTypes.string,
  }),
  currentLevel: PropTypes.shape({
    id: PropTypes.string,
    raw_lexile: PropTypes.number,
    header_id: PropTypes.number,
  }),
};

const QuizContainer = ({
  closeButtonEventProperties,
  currentAssignment,
  currentLevel,
  language,
  data,
  inProgressQuizzes,
  isBannerModeActive,
  onClickCloseButton = () => null,
  shouldFocusCloseButtonRef,
  unlockLevel,
  updateinProgressQuizzes,
  userProperties,
  assignments,
  contentId,
  user,
}) => {
  const { id: currentLevelId, raw_lexile: currentLevelRawLexile } =
    currentLevel;
  const { isStudent, isAuthenticatedUser, isAuthorizedTeacher, isTeacher } =
    userProperties;
  const router = useRouter();
  const {
    pathname,
    query,
    query: { levelId: paramLevelId, quizId, question: questionPosition },
  } = router;

  /* Default to using currentLevelId from the props over the levelId query param */
  const levelId = currentLevelId || paramLevelId;

  const [
    fetchStudentQuiz,
    {
      data: studentQuizDataArray,
      data: [studentQuizData] = studentQuizDataArray || [],
      loading: fetchStudentQuizLoading,
    },
  ] = useLazyStudentQuizGet({
    associated_content_id: levelId,
    quiz_id: data?.id,
  });
  const [
    createStudentQuiz,
    { loading: createStudentQuizLoading, error: createStudentQuizError },
  ] = useStudentQuizCreate();

  const [skippedQuestions, updateSkippedQuestions] = useState([]);
  const [showAnswersInPresentMode, setShowAnswersInPresentMode] =
    useState(false);

  const [
    postQuestionFlag,
    { data: questionFlagRequestData, loading: questionFlagRequestLoading },
  ] = useQuestionFlagPost();
  const [isFlaggingActivityQuestion, setisFlaggingActivityQuestion] =
    useState(false);
  const [selectedFlag, setSelectedFlag] = useState(null);
  const [displayQuestionFlagError, setDisplayQuestionFlagError] =
    useState(false);
  const [questionFlagRequestSuccessful, setQuestionFlagRequestSuccessful] =
    useState(false);

  const { data: formativeJwtResponse, loading: jwtReponseLoading } =
    useFormativeWebTokenGet(
      { contentId, quizId: data?.id, rawLexile: currentLevelRawLexile },
      !isTeacher,
    );

  const warningWrapperRef = useRef();

  const {
    articleData: challengeArticleData,
    refetch: refetchChallengeArticle,
  } = useChallengeArticleContext();

  useEffect(() => {
    if (createStudentQuizError) {
      logSubmissionError({
        message: "Unable to submit Quiz response",
        context: {
          activityId: quizId,
          contentId,
          currentLevel,
          user,
          assignmentIds: getAssignmentContentIds(assignments),
          url: router.asPath,
        },
      });
    }
  }, [
    createStudentQuizError,
    quizId,
    contentId,
    currentLevel,
    user,
    assignments,
    router.asPath,
  ]);

  useEffect(() => {
    isStudent && fetchStudentQuiz();
  }, [levelId, data?.id]);

  /**
   * If a completed student quiz has been returned, redirect to the user to the
   * summary screen. If the user is already on the summary screen,
   * redirect the user to the first quiz question.
   * The user remains on the same numbered question otherwise
   */

  useEffect(() => {
    const answersWereFetched = !!studentQuizData?.answers?.length;
    const href = {
      pathname,
      query: {
        ...query,
      },
    };

    if (answersWereFetched) {
      unlockLevel();
      delete href.query.question;
    } else if (!answersWereFetched) {
      href.query.question = 1;
    }

    router.replace(href, null, { shallow: true });
  }, [JSON.stringify(studentQuizData)]);

  // Clear skippedQuestions if the current article level changes
  useEffect(() => {
    updateSkippedQuestions([]);
  }, [data?.grade_band]);

  const { focusElement } = useFocusManager();

  /**
   * When a change is detected in the 'question' param, focus will be set on
   * the back button in the panel header
   **/
  useEffect(() => {
    focusElement(focusElementRefKeys.activityPanelBackButton);
  }, [router.query.question]);

  useEffect(() => {
    if (warningWrapperRef?.current) {
      warningWrapperRef?.current.scrollIntoView({
        behavior: "auto",
        block: "nearest",
      });
      warningWrapperRef?.current.focus();
    }
  }, [skippedQuestions]);

  // Toggle Show/Hide answers in present mode
  useEffect(() => {
    setShowAnswersInPresentMode(!isBannerModeActive && isAuthorizedTeacher);
  }, [isBannerModeActive]);

  useEffect(() => {
    if (questionFlagRequestLoading) {
      setQuestionFlagRequestSuccessful(false);
    } else {
      setQuestionFlagRequestSuccessful(!!questionFlagRequestData?.date_created);
    }
  }, [questionFlagRequestLoading]);

  // Reset the selected flag
  useEffect(() => {
    setSelectedFlag(null);
    setQuestionFlagRequestSuccessful(false);
  }, [isFlaggingActivityQuestion, questionPosition]);

  // The user is a student, answers have been returned, and the fetched quiz matches the currently selected quiz
  const isReviewMode =
    isStudent &&
    !!studentQuizData?.answers?.length &&
    (studentQuizData?.quiz?.id ?? null) === data?.id;
  const isPreviewMode = !isStudent && isAuthenticatedUser;

  // hidden quiz answers
  const shouldHideQuizAnswers =
    getAssignmentSetting("hide_quiz_answers", currentAssignment) && isStudent;

  const onClickBackButton = () => {
    const query = { ...router.query };
    // Remove all query params associated with the quiz.
    delete query.activityPanel;
    delete query.question;
    delete query.quizId;
    // Without the quiz query params, the user will return to the activity picker.
    router.replace({ pathname: router.pathname, query }, null, {
      shallow: true,
    });
  };

  const questionIndex = questionPosition - 1;

  // determine if there is a custom quiz
  const isCustomQuiz = !!currentAssignment?.activity_element_selections;
  const currentQuestions = getCurrentQuizQuestions({
    currentAssignment,
    data,
    isCustomQuiz,
    quizId,
  });

  const createStudentQuizAndRefetch = useCallback(
    async (payload) => {
      const result = await createStudentQuiz(payload);
      await fetchStudentQuiz();
      return result;
    },
    [fetchStudentQuiz, createStudentQuiz],
  );

  const submitStudentQuiz = async () => {
    const answers = [];
    const skippedAnswers = [];

    currentQuestions.forEach((question, index) => {
      const quizQuestions = inProgressQuizzes[data.id]?.quizQuestions;
      const { id, position } = question;

      if (quizQuestions && quizQuestions[id]) {
        // Build the answers to be submitted.
        answers.push({
          date_answered: new Date().toISOString(),
          quiz_question_id: id,
          value: quizQuestions[id]?.selectedAnswer,
        });
      } else {
        const pos = isCustomQuiz ? index + 1 : position;
        // If there are any empty values in the answer list, we display <QuizWarningMessage />
        skippedAnswers.push({
          position: pos,
          quiz_question_id: id,
        });
      }
    });

    if (skippedAnswers.length === 0) {
      const currentDate = new Date().toISOString();
      const assignmentIds = getAssignmentContentIds(assignments);

      const payload = {
        answers,
        assignment_ids: assignmentIds,
        associated_content_id: levelId || currentLevelId,
        date_started: currentDate,
        date_completed: currentDate,
        quiz_id: data.id,
      };

      if (!challengeArticleData?.data) {
        payload.is_active_challenge = false;
      }

      createStudentQuizAndRefetch(payload).then(() => {
        refetchChallengeArticle?.();
      });
    } else {
      updateSkippedQuestions([...skippedAnswers]);
    }
  };

  const submitQuestionFlag = async () => {
    const payload = {
      activityId: data.id,
      message: selectedFlag,
      questionId: currentQuestions[questionIndex].id,
    };

    if (selectedFlag) {
      setDisplayQuestionFlagError(false);
      postQuestionFlag(payload);
    } else {
      setDisplayQuestionFlagError(true);
    }
  };

  const onClickNavigationButtons = (navigateTo) => {
    if (isBannerModeActive)
      setShowAnswersInPresentMode(showAnswersInPresentMode);

    const href = {
      pathname,
      query: {
        ...query,
        question:
          navigateTo === "next"
            ? parseInt(questionPosition) + 1
            : parseInt(questionPosition) - 1,
      },
    };

    switch (navigateTo) {
      case "next":
      case "back":
        router.replace(href, null, { shallow: true });
        break;
      case "submit":
        if (isReviewMode) {
          delete href.query.question;
          router.replace(href, null, { shallow: true });
        } else if (isFlaggingActivityQuestion) {
          isAuthorizedTeacher && submitQuestionFlag();
        } else {
          isStudent && submitStudentQuiz();
        }
        break;
      case "cancelReport":
        setisFlaggingActivityQuestion(false);
        setDisplayQuestionFlagError(false);
        break;
      case "review":
      default:
        href.query.question = 1;
        router.replace(href, null, { shallow: true });
    }
  };

  const backButtonEventProperties = {
    objectType: objectTypes.quiz,
    actionName: actionNames.navigate,
    streamProperties: {
      content_type: "Article",
      description: "Navigate back to Activity Picker from quiz",
      content_id: levelId,
      object_id: quizId,
    },
  };

  const setQuizChoiceSelection = (choiceContentId) => {
    const question = currentQuestions?.[questionIndex];
    const questionId = question.id;

    const choiceSelection = {
      [questionId]: {
        selectedAnswer: choiceContentId,
      },
    };

    updateinProgressQuizzes({
      ...inProgressQuizzes,
      [data.id]: {
        quizQuestions: {
          ...inProgressQuizzes[data.id]?.quizQuestions,
          ...choiceSelection,
        },
      },
    });

    if (skippedQuestions.length !== 0) {
      const skippedAnswers = [...skippedQuestions].filter((answer) => {
        return answer.quiz_question_id !== questionId;
      });
      updateSkippedQuestions([...skippedAnswers]);
    }
  };

  const showSummary = isReviewMode && !questionPosition;
  const showQuestion = !showSummary && !isNaN(Number(questionPosition));
  const showReportActivityForm =
    isAuthorizedTeacher && isFlaggingActivityQuestion && showQuestion;

  const canSeeFormative = userProperties?.canSeeFormative;
  const shouldDisplayFormative =
    canSeeFormative && isTeacher && !jwtReponseLoading && formativeJwtResponse;

  const formativeButtonEventProperties = {
    objectType: objectTypes.user,
    actionName: actionNames.navigate,
    streamProperties: {
      content_type: "Article",
      description: "Opens Formative via the student quiz",
      content_id: levelId,
      object_id: quizId,
    },
  };

  const renderQuizSummary = () => {
    // Ensure answer/questions align based on quiz customization
    const { answers, questions } = getAlignedQuizQuestionsAndAnswers(
      isCustomQuiz,
      studentQuizData?.answers,
      currentQuestions,
    );

    return showSummary && data ? (
      <>
        <div css={[containerCss, scrollContainerCss]}>
          <QuizSummary
            answers={answers}
            questions={questions}
            quizId={quizId}
            shouldHideQuizAnswers={shouldHideQuizAnswers}
            isCustomQuiz={isCustomQuiz}
          />
        </div>
        <NavigationButtons>
          <NavigationButtons.QuizNavigationButtons
            handleClick={onClickNavigationButtons}
            isReviewMode={isReviewMode}
            isSummary
            contentId={levelId}
            objectId={quizId}
          />
        </NavigationButtons>
      </>
    ) : null;
  };

  const renderPresentModeButton = () => {
    if (!isBannerModeActive) return null;

    return (
      <QuizToggleAnswersButton
        showAnswersInPresentMode={showAnswersInPresentMode}
        questionId={currentQuestions[questionIndex].id}
        contentId={levelId}
        handleOnClick={() =>
          setShowAnswersInPresentMode(
            (showAnswersInPresentMode) => !showAnswersInPresentMode,
          )
        }
      />
    );
  };

  const renderQuestionFlagButton = () => {
    if (!isAuthorizedTeacher || !currentQuestions) return null;

    return (
      <QuestionFlagButton
        activityType="quiz"
        handleOnClick={() =>
          setisFlaggingActivityQuestion(
            (isFlaggingActivityQuestion) => !isFlaggingActivityQuestion,
          )
        }
        levelId={levelId}
        objectId={currentQuestions[questionIndex]?.id}
      />
    );
  };

  const renderQuestionFlagForm = () => {
    if (!currentQuestions) return null;

    const currentQuestion = currentQuestions[questionIndex];

    return showReportActivityForm && data ? (
      <>
        <div css={[containerCss, scrollContainerCss]}>
          <ItemCount
            count={currentQuestions.length}
            currentItem={questionPosition}
            isCompact={!isStudent}
          />
          <QuizQuestionText
            isStatic
            questionPosition={String(currentQuestion?.position)}
            text={currentQuestion?.raw_question}
          />
          <QuestionFlagContainer
            activityType="quiz"
            displayErrorMessage={displayQuestionFlagError}
            handleChange={setSelectedFlag}
            isRequestLoading={questionFlagRequestLoading}
            isRequestSuccessful={questionFlagRequestSuccessful}
            selectedFlag={selectedFlag}
          />
        </div>
        <NavigationButtons>
          <NavigationButtons.QuestionFlaggingNavigationButtons
            activityObjectType={objectTypes.quizQuestion}
            contentId={levelId}
            handleClick={onClickNavigationButtons}
            isDisabled={!selectedFlag}
            isReportSubmitted={questionFlagRequestSuccessful}
            questionId={currentQuestion?.id}
          />
        </NavigationButtons>
      </>
    ) : null;
  };

  const renderQuizElements = () => {
    if (fetchStudentQuizLoading || createStudentQuizLoading) {
      return <LoadingSpinner />;
    }

    return (
      <>
        {renderQuizSummary()}
        {renderQuestionFlagForm()}
        {showQuestion && data && !showReportActivityForm ? (
          <>
            <div css={[containerCss, scrollContainerCss]} tabIndex={0}>
              {shouldDisplayFormative && (
                <FormativeBanner
                  data={formativeJwtResponse}
                  eventProperties={formativeButtonEventProperties}
                />
              )}
              <ItemCount
                count={currentQuestions.length}
                currentItem={questionPosition}
                isCompact={!isStudent}
                question={currentQuestions[questionIndex]}
              />
              <QuizQuestion
                answerData={studentQuizData}
                contentId={data.id}
                currentLevelId={levelId}
                language={language}
                isReviewMode={isReviewMode}
                isPreviewMode={isPreviewMode}
                isStudent={isStudent}
                question={currentQuestions[questionIndex]}
                questionPosition={questionPosition}
                selectedAnswers={inProgressQuizzes}
                shouldHideQuizAnswers={shouldHideQuizAnswers}
                showAnswersInPresentMode={showAnswersInPresentMode}
                updateSelectedAnswers={setQuizChoiceSelection}
              />

              {renderPresentModeButton()}
              {renderQuestionFlagButton()}

              {!isReviewMode && skippedQuestions && (
                <div ref={warningWrapperRef}>
                  <QuizWarningMessage
                    skippedQuestions={skippedQuestions}
                    quizId={quizId}
                    isCustomQuiz={isCustomQuiz}
                  />
                </div>
              )}

              {isTeacher && !isAuthorizedTeacher && (
                <Toast __cssFor={walledTeacherToastCSS} showIcon>
                  <p>
                    To see the correct answers, first{" "}
                    <a href="/account/settings/">
                      complete your teacher account
                    </a>
                    .
                  </p>
                </Toast>
              )}
            </div>
            <NavigationButtons>
              <NavigationButtons.QuizNavigationButtons
                currentQuestion={String(questionPosition)}
                handleClick={onClickNavigationButtons}
                isStudent={isStudent}
                isReviewMode={isReviewMode}
                objectId={quizId}
                contentId={levelId}
                questionCount={currentQuestions.length}
              />
            </NavigationButtons>
          </>
        ) : null}
      </>
    );
  };

  return (
    <div css={panelWrapperCss}>
      <PanelHeader
        backButtonEventProperties={backButtonEventProperties}
        closeButtonEventProperties={closeButtonEventProperties}
        shouldFocusCloseButtonRef={shouldFocusCloseButtonRef}
        onClickBackButton={onClickBackButton}
        onClickCloseButton={onClickCloseButton}
      >
        <h2 css={titleCss}>Quiz</h2>
      </PanelHeader>
      {renderQuizElements()}
    </div>
  );
};

QuizContainer.propTypes = propTypes;

export default QuizContainer;
